import ArrowDownOutlined from '@ant-design/icons/ArrowDownOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';
import UserAddOutlined from '@ant-design/icons/UserAddOutlined';
import Button from 'antd/es/button';
import Dropdown from 'antd/es/dropdown';
import Modal from 'antd/es/modal';
import Skeleton from 'antd/es/skeleton';
import theme from 'antd/es/theme';
import Typography from 'antd/es/typography';
import uniqBy from 'lodash.uniqby';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { styled } from 'styled-components';

import ManageTeam from '@components/ManageTeam';
import { useTeamUsersQuery } from '@queries/team-users';
import { useTeamsQuery } from '@queries/teams';
import { useSessionUserId } from '@utils/hooks';
import { useTeamId } from '@utils/router';

const StyledButton = styled(Button)`
  margin-left: 4px;
  margin-right: 4px;
  &.collapsed {
    & > span {
      display: none;
    }
    & > span:nth-of-type(1) {
      display: inline;
    }
  }
`;

const TeamSettingsButton = ({
  teamId,
  closeDropdown,
}: {
  teamId: string | null | undefined;
  closeDropdown: () => void;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!teamId) {
    return <Button size="small" icon={<SettingOutlined />} disabled />;
  }

  return (
    <>
      <Modal
        title={`Team Settings`}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        maskClosable
        closable
        footer={null}
        width={800}
      >
        <ManageTeam teamId={teamId} />
      </Modal>
      <Button
        size="small"
        icon={<SettingOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          setIsModalOpen(true);
          closeDropdown();
        }}
      >
        Settings
      </Button>
    </>
  );
};

const TeamSelect = ({ collapsed }: { collapsed: boolean }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { pathname } = useLocation();
  const { userId: sessionUserId } = useSessionUserId();

  const { token } = theme.useToken();

  const teamId = useTeamId();

  const teamUsersQuery = useTeamUsersQuery(
    { userIds: sessionUserId ? [sessionUserId] : [] },
    !!sessionUserId,
  );
  const teamIds = teamUsersQuery.data?.map((teamUser) => teamUser.teamId);

  const teamsQuery = useTeamsQuery(
    { teamIds: teamIds ?? [] },
    !!teamIds?.length,
  );

  const urlTeamsQuery = useTeamsQuery(
    { teamIds: teamId ? [teamId] : [] },
    !!teamId,
  );

  if (teamsQuery.isLoading || urlTeamsQuery.isLoading) {
    return (
      <Skeleton.Button
        active
        style={{
          width: '100%',
        }}
      />
    );
  }

  const teams = uniqBy(
    [...(teamsQuery.data ?? []), ...(urlTeamsQuery.data ?? [])],
    ({ teamId }: { teamId: string }) => teamId,
  );
  const selectedTeam = teams.find((team) => team.teamId === teamId);

  if (!teams.length) {
    return <Button type="primary">No teams</Button>;
  }

  return (
    <>
      <Dropdown
        trigger={['click']}
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        menu={{
          items: [
            {
              key: 'actions',
              type: 'group',
              label: (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <TeamSettingsButton
                    teamId={teamId}
                    closeDropdown={() => setDropdownOpen(false)}
                  />
                  <Button
                    size="small"
                    icon={<UserAddOutlined />}
                    disabled
                    onClick={(e) => e.stopPropagation()}
                  >
                    Invite Members
                  </Button>
                </div>
              ),
            },
            {
              key: 'divider',
              type: 'divider',
            },
            ...teams.map((team: { teamId: string; name: string }) => {
              let link: string;
              if (team.teamId === teamId) {
                link = pathname;
              } else {
                link = `/team/${team.teamId}`;
              }
              return {
                key: team.teamId,
                label: <Link to={link}>{team.name}</Link>,
                title: "",  
              };
            }),
          ],
        }}
      >
        <StyledButton
          type="primary"
          style={{
            borderRadius: token.borderRadiusLG,
            boxShadow: '0px 0px 0px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'space-between',
            width: 'calc(100% - 8px)',
          }}
          className={collapsed ? 'collapsed' : ''}
        >
          <TeamOutlined />
          <Typography.Text
            ellipsis
            style={{
              color: token.colorTextLightSolid,
            }}
          >
            {`Team: ${selectedTeam?.name ?? 'No Team Selected'}`}
          </Typography.Text>

          <ArrowDownOutlined />
        </StyledButton>
      </Dropdown>
    </>
  );
};

export default TeamSelect;
